import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import {
  ApproveSimulationResultDocument,
  DocumentStatus,
  GetEditObjectFormResponseDocument,
  InsuranceDocument,
  SendTransactionalEmailDocument,
  Services,
  TransactionalEmailEvents,
  RecedeDocumentStageDocument,
  AddCommercialResponsibleDocument,
  AddOperationalResponsibleDocument,
  Result,
  ArchiveDocumentDocument,
  FinishSimulationDocument,
  DeleteSimulationResultDocument,
  AddOriginatorDocument,
  RetrySimulationResultDocument,
  useRedoQuotationsMutation,
  useSetPaymentDoneMutation,
  useSetDocumentAsSentMutation,
  useSelectResultsMutation,
  useSetQuotationCommentMutation,
  useArchiveProposalMutation,
  useGetShareTokenLazyQuery,
  useGetComplementaryDataFormResponseLazyQuery,
  useDeletePaymentMutation,
} from "../graphql/__GENERATED__/types";
import { useApolloClient } from "@apollo/client";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useAuth } from "../hooks/useAuth";
import { downloadFile } from "../services/models";
import { ModelNames } from "../models";
import { openFile } from "../helpers/openFile";
import useConfirmedAction from "../hooks/useConfirmedAction";
import useDebounce from "../hooks/useDebounce";
import { toComplementaryDataFormLocation } from "../Locations";

interface DocumentDetailsContextValue {
  refetch: () => Promise<void>;
  documentData: InsuranceDocument;
  setSelectedResultId: (id: string) => void;
  selectedResultId: string;
  orderedResults: Result[];
  setOrderedResults: (results: Result[]) => void;
  quotationComment: string;
  setQuotationComment: (comment: string) => void;
  handleSelectResults: (
    resultsIds: string[],
    justificationMessage: string
  ) => Promise<void>;

  handleApproveResult: (resultId: string) => Promise<void>;
  copyQuotationFormUrl: () => Promise<void>;
  handleSendEmail: (event: TransactionalEmailEvents) => Promise<void>;
  handleChangeDocumentStage: (docStatus: DocumentStatus) => Promise<void>;
  handleAssignCommercialResponsible: (contactId: string) => Promise<void>;
  handleAssignOperationalResponsible: (contactId: string) => Promise<void>;
  handleAssignOriginator: (contactId: string) => Promise<void>;
  handleArchive: () => Promise<void>;
  handleFinishQuotation: () => Promise<void>;
  redirectToQuotationFormUrl: () => Promise<void>;
  handleDeleteResult: (resultId: string) => Promise<void>;
  handleEditQuotationResult: (resultId: string) => void;
  editingQuotationResultId: string;
  handleRetrySimulationResult: (resultId: string) => Promise<void>;
  handleRedoQuotations: () => Promise<void>;
  handleSetPaymentDone: (paymentId: string) => Promise<void>;
  handleDeletePayment: (paymentId: string) => Promise<void>;
  handleSetDocumentsAsSent: () => Promise<void>;
  handleArchiveProposal: () => Promise<void>;
  handleCopyComplementaryFormLink: () => Promise<void>;
  // download functions
  handleFirstBillDownload: () => Promise<void>;
  handleKmlDownload: () => Promise<void>;
  handleDownloadComparisonPdf: () => Promise<void>;
  handleDownloadProposalPdf: () => Promise<void>;
}
export const DocumentDetailsContext =
  React.createContext<DocumentDetailsContextValue>({
    documentData: {
      cropId: "",
      farmerId: "",
      propertyId: "",
    },
    setSelectedResultId: () => {},
    selectedResultId: "",
    orderedResults: [],
    setOrderedResults: () => {},
    quotationComment: "",
    setQuotationComment: () => {},
    refetch: async () => {},
    handleSelectResults: async () => {},
    handleDownloadComparisonPdf: async () => {},
    handleDownloadProposalPdf: async () => {},
    handleApproveResult: async () => {},
    copyQuotationFormUrl: async () => {},
    handleSendEmail: async () => {},
    handleChangeDocumentStage: async () => {},
    handleAssignCommercialResponsible: async () => {},
    handleAssignOperationalResponsible: async () => {},
    handleAssignOriginator: async () => {},
    handleArchive: async () => {},
    handleFinishQuotation: async () => {},
    redirectToQuotationFormUrl: async () => {},
    handleDeleteResult: async () => {},
    handleEditQuotationResult: async () => {},
    handleRetrySimulationResult: async () => {},
    editingQuotationResultId: "",
    handleKmlDownload: async () => {},
    handleRedoQuotations: async () => {},
    handleFirstBillDownload: async () => {},
    handleSetPaymentDone: async () => {},
    handleDeletePayment: async () => {},
    handleSetDocumentsAsSent: async () => {},
    handleArchiveProposal: async () => {},
    handleCopyComplementaryFormLink: async () => {},
  });

interface DocumentDetailsProviderProps {
  children: ReactNode;
  documentData: InsuranceDocument;
  refetch: () => Promise<any>;
}

const DocumentDetailsProvider: FC<DocumentDetailsProviderProps> = ({
  children,
  documentData,
  refetch,
}) => {
  const auth = useAuth();
  const apolloClient = useApolloClient();
  const { errorHandler } = useErrorHandler();

  const [selectResultsMutation] = useSelectResultsMutation();
  const handleSelectResults = useCallback(
    async (resultsIds: string[]) => {
      try {
        await selectResultsMutation({
          variables: {
            docId: documentData._id!,
            resultsIds,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível selecionar os resultados"));
      }
    },
    [documentData._id]
  );

  const [setQuotationCommentMutation] = useSetQuotationCommentMutation();
  const handleSetComment = useCallback(
    async (message: string) => {
      try {
        await setQuotationCommentMutation({
          variables: {
            docId: documentData._id!,
            message,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível selecionar os resultados"));
      }
    },
    [documentData._id]
  );

  const handleDownloadFile = useCallback(
    async (fileKey: string) => {
      const file = await downloadFile(
        auth,
        ModelNames.DOCUMENTS,
        documentData._id!,
        fileKey
      );
      openFile(file);
    },
    [documentData._id, auth]
  );

  const handleDownloadComparisonPdf = useCallback(
    async () => handleDownloadFile("comparisonPdf"),
    [handleDownloadFile]
  );

  const handleDownloadProposalPdf = useCallback(
    async () => handleDownloadFile("proposalFile"),
    [handleDownloadFile]
  );

  const handleKmlDownload = useCallback(
    async () => handleDownloadFile("kmlFile"),
    [handleDownloadFile]
  );

  const handleFirstBillDownload = useCallback(
    async () => handleDownloadFile("firstBillFile"),
    [handleDownloadFile]
  );

  const handleApproveResult = useCallback(
    async (resultId: string) => {
      try {
        await apolloClient.mutate({
          mutation: ApproveSimulationResultDocument,
          variables: {
            docId: documentData._id,
            resultId: resultId,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível aprovar a cotação"));
      }
    },
    [documentData._id]
  );

  const [getShareTokenQuery] = useGetShareTokenLazyQuery();
  const getShareToken = useCallback(async () => {
    try {
      const { data } = await getShareTokenQuery({
        variables: {
          service: Services.Documents,
          objectId: documentData._id!,
        },
      });
      return data!.getShareToken;
    } catch {
      errorHandler(new Error("Não foi possível gerar o link do formulário"));
    }
  }, [documentData._id, getShareTokenQuery]);

  const generateQuotationFormUrl = useCallback(async () => {
    try {
      const {
        data: { getEditObjectFormResponse: formResponseId },
      } = await apolloClient.query({
        query: GetEditObjectFormResponseDocument,
        variables: {
          service: Services.Documents,
          formId: "cotacao",
          objectId: documentData._id,
        },
      });
      const token = await getShareToken();
      const formLink = `/cotacoes/form/cotacao/${formResponseId}?token=${token}`;
      return formLink;
    } catch {
      errorHandler(new Error("Não foi possível gerar o link do formulário"));
    }
  }, [documentData._id]);

  const [getComplementaryFormResponseQuery] =
    useGetComplementaryDataFormResponseLazyQuery();

  const generateComplementaryDataFormUrl = useCallback(async () => {
    try {
      const { data } = await getComplementaryFormResponseQuery({
        variables: {
          objectId: documentData._id!,
        },
      });
      const {
        getComplementaryDataFormResponse: { formResponseId },
      } = data!;
      const token = await getShareToken();
      const formLink = `${toComplementaryDataFormLocation(
        formResponseId,
        token!
      )}`;
      return formLink;
    } catch {
      errorHandler(new Error("Não foi possível gerar o link do formulário"));
    }
  }, [documentData._id]);

  const redirectToQuotationFormUrl = useCallback(async () => {
    try {
      const formLink = await generateQuotationFormUrl();
      window.open(formLink, "_blank");
    } catch {
      errorHandler(
        new Error("Não foi possível redirecionar para o formulário")
      );
    }
  }, [documentData._id]);

  const copyLinkToClipboard = useCallback(async (link: string) => {
    try {
      const { protocol, host } = window.location;
      const shareLink = `${protocol}//${host}${link}`;
      await navigator.clipboard.writeText(shareLink);
      alert(`Link de compartilhamento copiado!`);
    } catch {
      errorHandler(new Error("Não foi possível copiar o link do formulário"));
    }
  }, []);

  const copyQuotationFormUrl = useCallback(async () => {
    try {
      const formLink = await generateQuotationFormUrl();
      await copyLinkToClipboard(formLink!);
    } catch {
      errorHandler(new Error("Não foi possível copiar o link do formulário"));
    }
  }, [documentData._id, copyLinkToClipboard]);

  const handleCopyComplementaryFormLink = useCallback(async () => {
    try {
      const formLink = await generateComplementaryDataFormUrl();
      await copyLinkToClipboard(formLink!);
    } catch {
      errorHandler(new Error("Não foi possível copiar o link do formulário"));
    }
  }, [documentData._id]);

  const handleSendEmail = useCallback(
    async (event: TransactionalEmailEvents) => {
      try {
        await apolloClient.mutate({
          mutation: SendTransactionalEmailDocument,
          variables: {
            docId: documentData._id,
            event,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível enviar o email"));
      }
    },
    [documentData._id]
  );

  const handleChangeDocumentStage = useCallback(
    async (docStatus: DocumentStatus) => {
      try {
        await apolloClient.mutate({
          mutation: RecedeDocumentStageDocument,
          variables: {
            service: Services.Documents,
            docId: documentData._id,
            docStatus: docStatus,
          },
        });
        refetch();
      } catch {
        errorHandler(
          new Error("Não foi possível alterar o estágio do documento")
        );
      }
    },
    [documentData._id]
  );

  const handleAssignCommercialResponsible = useCallback(
    async (contactId: string) => {
      try {
        await apolloClient.mutate({
          mutation: AddCommercialResponsibleDocument,
          variables: {
            service: Services.Documents,
            docId: documentData._id,
            responsibleId: contactId,
          },
        });
        refetch();
      } catch {
        errorHandler(
          new Error("Não foi possível atribuir um responsável comercial")
        );
      }
    },
    [documentData._id]
  );

  const handleAssignOperationalResponsible = useCallback(
    async (contactId: string) => {
      try {
        await apolloClient.mutate({
          mutation: AddOperationalResponsibleDocument,
          variables: {
            service: Services.Documents,
            docId: documentData._id,
            responsibleId: contactId,
          },
        });
        refetch();
      } catch {
        errorHandler(
          new Error("Não foi possível atribuir um responsável operacional")
        );
      }
    },
    [documentData._id]
  );

  const handleAssignOriginator = useCallback(
    async (contactId: string) => {
      try {
        await apolloClient.mutate({
          mutation: AddOriginatorDocument,
          variables: {
            service: Services.Documents,
            docId: documentData._id,
            originatorId: contactId,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível atribuir um parceiro"));
      }
    },
    [documentData._id]
  );

  const handleArchive = useCallback(async () => {
    try {
      await apolloClient.mutate({
        mutation: ArchiveDocumentDocument,
        variables: {
          docId: documentData._id,
        },
      });
      refetch();
    } catch {
      errorHandler(new Error("Não foi possível arquivar o documento"));
    }
  }, []);

  const handleFinishQuotation = useCallback(async () => {
    try {
      await apolloClient.mutate({
        mutation: FinishSimulationDocument,
        variables: {
          service: Services.Documents,
          docId: documentData._id,
        },
      });
      refetch();
    } catch {
      errorHandler(new Error("Não foi possível finalizar a cotação"));
    }
  }, [documentData._id]);

  const handleDeleteResult = useCallback(
    async (resultId: string) => {
      try {
        await apolloClient.mutate({
          mutation: DeleteSimulationResultDocument,
          variables: {
            resultId: resultId,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível deletar a cotação"));
      }
    },
    [documentData._id]
  );

  const [orderedResults, setOrderedResults] = useState<Result[]>(
    () => documentData.selectedSimulationResults?.map((r) => r!) || []
  );
  const [quotationComment, setQuotationComment] = useState<string>(
    documentData.justificationMessage || ""
  );

  useEffect(() => {
    if (
      // Se os resultados não forem os mesmos e na mesma ordem
      (documentData.selectedSimulationResults?.length || 0) !==
        orderedResults.length ||
      !orderedResults.every(
        ({ _id }, index) =>
          _id == (documentData.selectedSimulationResults || [])[index]?._id
      )
    ) {
      handleSelectResults(orderedResults.map(({ _id }) => _id!));
    }
  }, [orderedResults]);

  const { debouncedValue: debouncedQuotationComment } = useDebounce(
    quotationComment,
    1000
  );
  useEffect(() => {
    if (
      (debouncedQuotationComment || documentData.justificationMessage) &&
      debouncedQuotationComment !== documentData.justificationMessage
    ) {
      handleSetComment(debouncedQuotationComment);
    }
  }, [debouncedQuotationComment]);

  const [selectedResultId, setSelectedResultId] = useState<string>("");

  const [editingQuotationResultId, setEditingQuotationResultId] =
    useState<string>("");

  const handleRetrySimulationResult = useCallback(
    async (resultId: string) => {
      try {
        await apolloClient.mutate({
          mutation: RetrySimulationResultDocument,
          variables: {
            resultId,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível tentar novamente a cotação"));
      }
    },
    [apolloClient]
  );

  const [redoQuotationsMutation] = useRedoQuotationsMutation();

  const handleRedoQuotations = useConfirmedAction(
    "Tem certeza? As cotações atuais serão removidas!",
    async () => {
      try {
        await redoQuotationsMutation({
          variables: {
            docId: documentData._id!,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível refazer as cotações"));
      }
    }
  );

  const [setPaymentDone] = useSetPaymentDoneMutation();

  const handleSetPaymentDone = useCallback(
    async (paymentId: string) => {
      try {
        await setPaymentDone({
          variables: {
            paymentId,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível dar baixa na parcela"));
      }
    },
    [documentData.payments]
  );

  const [deletePaymentMutation] = useDeletePaymentMutation();

  const handleDeletePayment = useCallback(
    async (paymentId: string) => {
      try {
        await deletePaymentMutation({
          variables: {
            paymentId,
          },
        });
        refetch();
      } catch {
        errorHandler(new Error("Não foi possível deletar a parcela"));
      }
    },
    [documentData.payments]
  );

  const [setDocumentAsSentMutation] = useSetDocumentAsSentMutation();

  const setDocumentsAsSent = useCallback(async () => {
    try {
      await setDocumentAsSentMutation({
        variables: {
          docId: documentData._id!,
        },
      });
      refetch();
    } catch {
      errorHandler(
        new Error("Não foi possível marcar o documento como enviado")
      );
    }
  }, [documentData._id]);

  const handleSetDocumentsAsSent = useConfirmedAction(
    "Tem certeza? O email não será enviado.",
    () => setDocumentsAsSent()
  );

  const [archiveProposalMutation] = useArchiveProposalMutation();

  const archiveProposal = useCallback(async () => {
    try {
      await archiveProposalMutation({
        variables: {
          docId: documentData._id!,
        },
      });
      refetch();
    } catch {
      errorHandler(new Error("Não foi possível arquivar a proposta"));
    }
  }, [documentData._id]);

  const handleArchiveProposal = useConfirmedAction(
    "Tem certeza que deseja arquivar a proposta?",
    () => archiveProposal()
  );

  return (
    <DocumentDetailsContext.Provider
      value={{
        refetch,
        documentData,
        selectedResultId,
        setSelectedResultId,
        orderedResults,
        setOrderedResults,
        quotationComment,
        setQuotationComment,
        handleSelectResults,
        handleDownloadComparisonPdf,
        handleDownloadProposalPdf,
        handleKmlDownload,
        handleFirstBillDownload,
        handleApproveResult,
        copyQuotationFormUrl,
        handleSendEmail,
        handleChangeDocumentStage,
        handleAssignCommercialResponsible,
        handleAssignOperationalResponsible,
        handleAssignOriginator,
        handleArchive,
        handleFinishQuotation,
        redirectToQuotationFormUrl,
        handleDeleteResult,
        editingQuotationResultId,
        handleEditQuotationResult: setEditingQuotationResultId,
        handleRetrySimulationResult,
        handleRedoQuotations,
        handleSetPaymentDone,
        handleSetDocumentsAsSent,
        handleArchiveProposal,
        handleCopyComplementaryFormLink,
        handleDeletePayment,
      }}
    >
      {children}
    </DocumentDetailsContext.Provider>
  );
};

export default DocumentDetailsProvider;
